import { IHttpService, IPromise, IRequestShortcutConfig } from "angular";
import { EnvironmentService } from "@gtmhub/env";
import { Comment, CommentStatsMap, CommentTargetType, CreateCommentDTO } from "@webapp/comments";
import { ICollection } from "@webapp/core/core.models";

export class CommentService {
  public static $inject = ["$http", "EnvironmentService"];

  constructor(private $http: IHttpService, private env: EnvironmentService) {}

  public getComments(params: {
    ids?: string;
    targetType?: CommentTargetType;
    targetIds?: string;
    mentionedId?: string;
    modifiedBy?: string;
    createdBy?: string;
    seen?: boolean;
    fields?: string;
    sort?: string;
  }): IPromise<ICollection<Comment>> {
    const url = this.env.getApiEndpoint("/comments");
    const query: IRequestShortcutConfig = { params };

    return this.$http.get<ICollection<Comment>>(url, query).then((response) => response.data);
  }

  public getComment(id: string): IPromise<Comment> {
    const url = this.env.getApiEndpoint("/comments/" + id);

    return this.$http.get<Comment>(url).then((response) => response.data);
  }

  public post(comment: CreateCommentDTO, metadata: object = {}): IPromise<Comment> {
    const url = this.env.getApiEndpoint("/comments");
    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          hasGif: !!comment.gif?.id,
          ...metadata,
        },
      },
    };

    return this.$http.post<Comment>(url, comment, gtmhubAdditionalParams).then((response) => response.data);
  }

  public edit(id: string, content: Partial<Comment>, gifChanges: { isDeleted: boolean; isAdded: boolean }, metadata: object = {}): IPromise<Comment> {
    const url = this.env.getApiEndpoint(`/comments/${id}`);

    const isGifEdited = gifChanges.isAdded || gifChanges.isDeleted;
    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          ...(isGifEdited && { gifEdited: gifChanges.isAdded ? "added" : "deleted" }),
          ...metadata,
        },
      },
    };

    return this.$http.put<Comment>(url, content, gtmhubAdditionalParams).then((response) => response.data);
  }

  public delete(id: string, metadata?: { [key: string]: string }): IPromise<void> {
    const url = this.env.getApiEndpoint(`/comments/${id}`);
    const gtmhubAdditionalParams: IRequestShortcutConfig = this.buildHeaderParamsFromMetadata(metadata);

    return this.$http.delete<void>(url, gtmhubAdditionalParams).then((response) => response.data);
  }

  public markAsSeen(id: string): IPromise<Comment> {
    const url = this.env.getApiEndpoint("/comments/" + id + "/see");

    return this.$http.post<Comment>(url, null).then((response) => response.data);
  }

  public markAllAsSeen(): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/comments/see-all");

    return this.$http.post<unknown>(url, null).then((response) => response.data);
  }

  public getCommentsStats(targetIds: string): IPromise<CommentStatsMap> {
    const url: string = this.env.getApiEndpoint("/comments/stats");
    const query: IRequestShortcutConfig = {
      params: {
        targetIds: targetIds,
      },
    };

    return this.$http.get<CommentStatsMap>(url, query).then((response) => response.data);
  }

  private buildHeaderParamsFromMetadata = (metadata): IRequestShortcutConfig => {
    if (metadata) {
      const gtmhubAdditionalParams: IRequestShortcutConfig = {
        params: {
          gtmhubAdditionalParams: {},
        },
      };

      Object.keys(metadata).forEach((param) => {
        gtmhubAdditionalParams.params.gtmhubAdditionalParams[param] = metadata[param];
      });

      return gtmhubAdditionalParams;
    }
  };
}
